import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { currency, fmtDate, countryFlag, PHASES, STAGE_BY_ID } from "@/lib/format";
import { Search, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Patients({ role }) {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/patients");
        setPatients(data);
      } catch (e) { toast.error(getApiErrorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return patients.filter((p) => {
      if (s && ![p.name, p.treatment, p.country].some((x) => (x || "").toLowerCase().includes(s))) return false;
      if (phaseFilter !== "All") {
        const stage = STAGE_BY_ID[p.stage];
        if (!stage || stage.phase !== phaseFilter) return false;
      }
      return true;
    });
  }, [patients, search, phaseFilter]);

  return (
    <div data-testid="patients-page">
      <PageHeader
        eyebrow="International patients"
        title="Patients"
        subtitle="All patients who have reached admission or beyond. Track their travel and treatment journey."
      />

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E9992]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, treatment, country…"
            data-testid="patients-search-input"
            className="w-full pl-9 pr-3 py-2.5 border border-[#E8E6E1] rounded-full bg-white text-sm focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]" />
        </div>
        <div className="flex gap-1 bg-white border border-[#E8E6E1] rounded-full p-1">
          {["All", "Treatment", "Closure"].map((p) => (
            <button key={p} onClick={() => setPhaseFilter(p)} data-testid={`patients-filter-${p.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${phaseFilter === p ? "bg-[#2A4D3E] text-white" : "text-[#5C6660] hover:bg-[#F7F5F2]"}`}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="text-sm text-[#5C6660]">Loading…</div> : (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#FDFCFB] border-b border-[#E8E6E1] text-xs font-semibold uppercase tracking-wider text-[#5C6660] text-left">
              <tr>
                <th className="px-5 py-3">Patient</th>
                <th className="px-5 py-3">Country</th>
                <th className="px-5 py-3">Treatment</th>
                <th className="px-5 py-3">Stage</th>
                <th className="px-5 py-3">Treatment value</th>
                <th className="px-5 py-3">Arrival</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} onClick={() => navigate(`/${role}/patients/${p.id}`)}
                  data-testid={`patient-row-${p.id}`}
                  className="border-b border-[#E8E6E1] last:border-0 hover:bg-[#F7F5F2] cursor-pointer transition">
                  <td className="px-5 py-3.5">
                    <div className="text-sm font-medium text-[#2A332C]">{p.name}</div>
                    <div className="text-[11px] text-[#8E9992]">{p.gender || ""} {p.age ? `· ${p.age}y` : ""}</div>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-[#5C6660]">{countryFlag(p.country)} {p.country || "—"}</td>
                  <td className="px-5 py-3.5 text-sm text-[#5C6660]">{p.treatment}</td>
                  <td className="px-5 py-3.5"><StatusBadge kind="stage" value={p.stage} /></td>
                  <td className="px-5 py-3.5 text-sm font-medium text-[#2A332C]">{currency(p.treatment_value)}</td>
                  <td className="px-5 py-3.5 text-xs text-[#8E9992]">{fmtDate(p.arrival_date)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="6" className="px-5 py-12 text-center text-sm text-[#8E9992]">No patients yet. They appear automatically once leads reach the admission stage.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
